// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import ConfirmationModal from '../../../../ui/overlays/modals/ConfirmationModal';
import type { TagsSectionProps } from './index';

interface WorkflowTag {
  id: number;
  name_en: string;
  name_ru: string;
  display_order: number;
  created_by: string;
  created_at: string;
  updated_at?: string;
  workflow_count?: number;
}

export default function TagsSection({
  workflowId,
  language,
  selectedTagIds,
  onTagIdsChange
}: TagsSectionProps) {
  const { getToken } = useAuth();
  const t = useLocalization(language);

  const [allTags, setAllTags] = useState<WorkflowTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create/Edit tag state
  const [editingTag, setEditingTag] = useState<{
    id?: number;
    name_en: string;
    name_ru: string;
    display_order: number;
  } | null>(null);

  // Delete confirmation
  const [deletingTagId, setDeletingTagId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [workflowId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken({ template: 'withmail' });

      // Load all tags
      const tagsResponse = await fetch('/api/admin/workflow-tags', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!tagsResponse.ok) {
        throw new Error('Failed to load tags');
      }

      const tagsData = await tagsResponse.json();
      setAllTags(tagsData.tags || []);

    } catch (error) {
      console.error('Error loading tags:', error);
      setError(error instanceof Error ? error.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTag = (tagId: number) => {
    const newIds = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter(id => id !== tagId)
      : [...selectedTagIds, tagId];
    onTagIdsChange(newIds);
  };

  const handleCreateTag = () => {
    setEditingTag({
      name_en: '',
      name_ru: '',
      display_order: allTags.length
    });
  };

  const handleEditTag = (tag: WorkflowTag) => {
    setEditingTag({
      id: tag.id,
      name_en: tag.name_en,
      name_ru: tag.name_ru,
      display_order: tag.display_order
    });
  };

  const handleSaveTag = async () => {
    if (!editingTag) return;

    try {
      setSaving(true);
      setError(null);

      const token = await getToken({ template: 'withmail' });

      const isCreate = !editingTag.id;
      const url = isCreate
        ? '/api/admin/workflow-tags'
        : `/api/admin/workflow-tags/${editingTag.id}`;

      const method = isCreate ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name_en: editingTag.name_en,
          name_ru: editingTag.name_ru,
          display_order: editingTag.display_order
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save tag');
      }

      setEditingTag(null);
      await loadData();

    } catch (error) {
      console.error('Error saving tag:', error);
      setError(error instanceof Error ? error.message : 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    try {
      setSaving(true);
      setError(null);

      const token = await getToken({ template: 'withmail' });

      const response = await fetch(`/api/admin/workflow-tags/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete tag');
      }

      setDeletingTagId(null);
      await loadData();

    } catch (error) {
      console.error('Error deleting tag:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete tag');
    } finally {
      setSaving(false);
    }
  };

  const getTagName = (tag: WorkflowTag) => {
    return language === 'ru' ? tag.name_ru : tag.name_en;
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Icon name={ICONS.TAG} size={20} className="text-indigo-400" />
            {t.admin?.tags || 'Tags'}
          </h3>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCreateTag();
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Icon name={ICONS.PLUS} size={14} />
            {t.adminWorkflows?.tags?.createNewTag || 'Create Tag'}
          </button>
        </div>
        <p className="text-sm text-gray-400">
          {t.admin?.tagsDesc || 'Select tags for this workflow.'}
        </p>
      </section>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Tags List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
        </div>
      ) : allTags.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700/50">
          <Icon name={ICONS.TAG} size={48} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 mb-4">
            {t.adminWorkflows?.tags?.noTags || 'No tags created yet'}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCreateTag();
            }}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
          >
            {t.adminWorkflows?.tags?.createTag || 'Create First Tag'}
          </button>
        </div>
      ) : (
        <section className="space-y-4">
          {/* Tags Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {allTags.map((tag) => (
              <div
                key={tag.id}
                onClick={() => handleToggleTag(tag.id)}
                className={`bg-gray-800/50 border rounded-lg p-3 transition-all cursor-pointer ${
                  selectedTagIds.includes(tag.id)
                    ? 'border-purple-500 bg-purple-600/10'
                    : 'border-gray-600/50 hover:border-gray-500'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedTagIds.includes(tag.id)
                        ? 'bg-purple-600 border-purple-600'
                        : 'border-gray-500'
                    }`}>
                      {selectedTagIds.includes(tag.id) && (
                        <Icon name={ICONS.CHECK} size={12} className="text-white" />
                      )}
                    </div>
                  </div>

                  {/* Tag Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">
                      {getTagName(tag)}
                    </h4>
                    <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                      <p>EN: {tag.name_en}</p>
                      <p>RU: {tag.name_ru}</p>
                      {tag.workflow_count !== undefined && (
                        <p className="text-purple-400">
                          {t.adminWorkflows?.tags?.usedByWorkflows?.replace('{count}', String(tag.workflow_count)) ||
                            `Used by ${tag.workflow_count} workflows`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditTag(tag);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                      title={t.adminWorkflows?.tags?.editTag || 'Edit'}
                    >
                      <Icon name={ICONS.EDIT} size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeletingTagId(tag.id);
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title={t.adminWorkflows?.tags?.deleteTag || 'Delete'}
                    >
                      <Icon name={ICONS.TRASH} size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Selected count */}
          <div className="pt-4 border-t border-gray-700/50">
            <span className="text-sm text-gray-400">
              {selectedTagIds.length === 0
                ? (t.adminWorkflows?.tags?.noTagsSelected || 'No tags selected')
                : `${selectedTagIds.length} ${selectedTagIds.length === 1 ? 'tag' : 'tags'} selected`
              }
            </span>
          </div>
        </section>
      )}

      {/* Edit/Create Tag Modal */}
      {editingTag && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            e.stopPropagation();
            setEditingTag(null);
          }}
        >
          <div
            className="bg-gray-800 border border-gray-600 rounded-lg shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                {editingTag.id
                  ? (t.adminWorkflows?.tags?.editTag || 'Edit Tag')
                  : (t.adminWorkflows?.tags?.createTag || 'Create Tag')
                }
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t.adminWorkflows?.tags?.tagNameEn || 'Name (English)'}
                  </label>
                  <input
                    type="text"
                    value={editingTag.name_en}
                    onChange={(e) => setEditingTag({ ...editingTag, name_en: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                    placeholder="AI Art"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t.adminWorkflows?.tags?.tagNameRu || 'Name (Russian)'}
                  </label>
                  <input
                    type="text"
                    value={editingTag.name_ru}
                    onChange={(e) => setEditingTag({ ...editingTag, name_ru: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                    placeholder="AI Арт"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t.adminWorkflows?.tags?.displayOrder || 'Display Order'}
                  </label>
                  <input
                    type="number"
                    value={editingTag.display_order}
                    onChange={(e) => setEditingTag({ ...editingTag, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingTag(null)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t.adminWorkflows?.tags?.cancel || 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveTag}
                  disabled={saving || !editingTag.name_en || !editingTag.name_ru}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving
                    ? (t.adminWorkflows?.urlManager?.saving || 'Saving...')
                    : (t.adminWorkflows?.urlManager?.save || 'Save')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deletingTagId && (
        <ConfirmationModal
          isOpen={true}
          title={t.adminWorkflows?.tags?.deleteTag || 'Delete Tag'}
          message={t.adminWorkflows?.tags?.confirmDelete || 'Delete this tag? It will be removed from all workflows.'}
          confirmLabel={t.adminWorkflows?.tags?.deleteTag || 'Delete'}
          cancelLabel={t.adminWorkflows?.tags?.cancel || 'Cancel'}
          onConfirm={() => handleDeleteTag(deletingTagId)}
          onCancel={() => setDeletingTagId(null)}
          variant="danger"
        />
      )}
    </div>
  );
}
